import { useState } from "react";
import AdminSlotSelect from "./AdminSlotSelect";

const COUPLE_EDIT_SECTIONS = [
  {
    title: "Couple & contact",
    fields: [
      { k: "couple_full_name", label: "Couple full name", type: "text" },
      { k: "email", label: "Email", type: "email" },
      { k: "mobile_number", label: "Mobile number", type: "text" },
      {
        k: "communication_preference",
        label: "Communication preference",
        type: "text",
      },
      { k: "heard_about_us", label: "How did you hear about us?", type: "text" },
    ],
  },
  {
    title: "Wedding details",
    fields: [
      { k: "wedding_date", label: "Wedding date (YYYY-MM-DD)", type: "text" },
      { k: "guest_count", label: "Guest count", type: "number" },
      { k: "wedding_type", label: "Wedding type (comma-separated)", type: "csv" },
      {
        k: "services_needed",
        label: "Services needed (comma-separated)",
        type: "csv",
      },
      { k: "overall_vibe", label: "Overall vibe / theme", type: "textarea" },
    ],
  },
  {
    title: "Venue & logistics",
    fields: [
      { k: "venue_address", label: "Venue address", type: "textarea" },
      {
        k: "venue_restrictions",
        label: "Venue restrictions",
        type: "textarea",
      },
      { k: "budget", label: "Budget", type: "text" },
      {
        k: "vendors_already_booked",
        label: "Vendors already booked",
        type: "textarea",
      },
    ],
  },
  {
    title: "Preferences & extras",
    fields: [
      { k: "priorities", label: "Priorities", type: "textarea" },
      { k: "must_haves", label: "Must-haves", type: "textarea" },
      { k: "social_handles", label: "Social handles", type: "text" },
      { k: "anything_else", label: "Anything else?", type: "textarea" },
    ],
  },
  {
    title: "Intro appointment",
    fields: [
      {
        k: "intro_slot",
        label: "Introduction appointment",
        type: "slot",
      },
    ],
  },
];

const EDITABLE_COUPLE_FIELDS = COUPLE_EDIT_SECTIONS.flatMap((s) => s.fields);

const CoupleEditForm = ({ row, onSave, onCancel }) => {
  const [form, setForm] = useState(() => {
    const initial = {};
    EDITABLE_COUPLE_FIELDS.forEach((f) => {
      if (f.type === "slot") {
        initial.intro_slot_date = row?.intro_slot_date || "";
        initial.intro_slot_time = row?.intro_slot_time || "";
        return;
      }
      const raw = row?.[f.k];
      initial[f.k] =
        f.type === "csv"
          ? Array.isArray(raw)
            ? raw.join(", ")
            : raw || ""
          : f.type === "number"
          ? raw === null || raw === undefined
            ? ""
            : String(raw)
          : raw || "";
    });
    return initial;
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const patch = {};
      EDITABLE_COUPLE_FIELDS.forEach((f) => {
        if (f.type === "slot") {
          patch.intro_slot_date = form.intro_slot_date || "";
          patch.intro_slot_time = form.intro_slot_time || "";
          return;
        }
        const v = form[f.k];
        if (f.type === "csv") {
          patch[f.k] = v
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        } else if (f.type === "number") {
          patch[f.k] = v === "" ? 0 : Number(v);
        } else {
          patch[f.k] = v;
        }
      });
      await onSave(patch);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid="couple-edit-form"
      onSubmit={submit}
      className="mt-6 space-y-8"
    >
      {COUPLE_EDIT_SECTIONS.map((section) => (
        <section key={section.title} className="space-y-3">
          <h4 className="font-accent text-[11px] tracking-[0.25em] uppercase text-[var(--ld-gold-deep)]">
            {section.title}
          </h4>
          <div className="space-y-3">
            {section.fields.map((f) => (
              <label key={f.k} className="block">
                <span className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
                  {f.label}
                </span>
                {f.type === "textarea" ? (
                  <textarea
                    data-testid={`couple-edit-${f.k}`}
                    value={form[f.k]}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, [f.k]: e.target.value }))
                    }
                    rows={3}
                    className="mt-1 w-full bg-transparent border border-[var(--ld-ink)]/25 px-3 py-2 text-sm text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
                  />
                ) : f.type === "slot" ? (
                  <div className="mt-1">
                    <AdminSlotSelect
                      testId={`couple-edit-${f.k}`}
                      date={form.intro_slot_date}
                      time={form.intro_slot_time}
                      onChange={({ date, time }) =>
                        setForm((s) => ({
                          ...s,
                          intro_slot_date: date,
                          intro_slot_time: time,
                        }))
                      }
                    />
                  </div>
                ) : (
                  <input
                    data-testid={`couple-edit-${f.k}`}
                    type={
                      f.type === "email"
                        ? "email"
                        : f.type === "number"
                        ? "number"
                        : "text"
                    }
                    value={form[f.k]}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, [f.k]: e.target.value }))
                    }
                    className="mt-1 w-full h-10 bg-transparent border border-[var(--ld-ink)]/25 px-3 text-sm text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
                  />
                )}
              </label>
            ))}
          </div>
        </section>
      ))}
      <div className="flex items-center gap-2 pt-2">
        <button
          type="submit"
          disabled={saving}
          data-testid="couple-edit-save"
          className="h-10 px-5 text-[12px] tracking-[0.18em] uppercase bg-[var(--ld-ink)] text-[var(--ld-ivory)] hover:bg-[var(--ld-gold-deep)] transition-colors disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          data-testid="couple-edit-cancel"
          onClick={onCancel}
          className="h-10 px-5 text-[12px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/40 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default CoupleEditForm;
